/*
 * Google Enhanced Ecommerce Object
 *
 * This module manages basket dependency rules, so that when a product is added or removed, any automatic basket
 * updates can also take place
 */
const rules = {

    /*
     * Process any rules attached to the element that should be triggered on "trigger"
     *
     * @param {object} DOM element
     * @param {string} trigger
     *
     * @returns {void}
     */
    processRules: (element, trigger) => {

        if (!gaEcommerce.core.checkStorageCapabilities()) return;

        if (typeof element.dataset?.ecomRules === "undefined") {
            return;
        }

        const rules = gaEcommerce.util.parseJsonArray(element.dataset.ecomRules),
              itemsToRemove = [],
              itemsToAdd = [];

        rules.reduce((filtered, rule) => {

            if (typeof rule.type !== 'undefined') {
                switch (rule.type) {
                    /*
                     * If our rule type is that something is "included",
                     * then we need to add this product if adding,
                     * otherwise we need to remove it, if removing
                     */
                    case 'included':
                        rule.action = trigger;
                        rule.triggerOn = trigger;
                        break;
                    case 'updateBundle':
                        rule.action = 'updateBundle';
                        rule.triggerOn = trigger;
                        break;
                }
            }
            if (rule.triggerOn === trigger) {
                filtered.push(rule);
            }
            return filtered;
        }, []).map(gaEcommerce.rules.processRule(itemsToRemove, itemsToAdd));

        gaEcommerce.util.getBasket().processItems(
            itemsToAdd, itemsToRemove, 'non-interaction', true
        );
    },

    /*
     * Curry a processRule function with the itemsToRemove and itemsToAdd arrays
     *
     * @param {array} itemsToRemove
     * @param {array} itemsToAdd
     *
     * @returns {function(object): void}
     */
    processRule: (itemsToRemove, itemsToAdd) =>
        (rule) => {
            switch (rule.action) {
                case 'add':
                    gaEcommerce.rules.processAddRule(rule, itemsToAdd);
                    break;
                case 'remove':
                    gaEcommerce.rules.processRemoveRule(rule, itemsToRemove);
                    break;
                case 'replace':
                    gaEcommerce.rules.processReplaceRule(rule, itemsToRemove, itemsToAdd);
                    break;
                case 'update':
                    gaEcommerce.rules.processUpdateRule(rule, itemsToRemove, itemsToAdd);
                    break;
                case 'updateBundle':
                    gaEcommerce.rules.processUpdateBundleRule(rule, itemsToRemove, itemsToAdd);
                    break;
                case 'updateActivation':
                    gaEcommerce.rules.processUpdateActivationRule(rule);
                    break;
            }
        }
    ,

    /*
     * Process a rule that might add a product
     *
     * @param {object} rule
     * @param {array} itemsToAdd
     *
     * @returns {void}
     */
    processAddRule: (rule, itemsToAdd) => {
        rule.items.map(function (item) {
            // @IMPORTANT Currently, only adding items by ID is supported
            const productElement = gaEcommerce.core.getProductElementById(item.id);

            if (productElement !== null) {
                itemsToAdd.push(productElement);
            }
        });
    },

    /*
     * Process a rule that might remove a product
     *
     * @param {object} rule
     * @param {array} itemsToRemove
     *
     * @returns {void}
     */
    processRemoveRule: (rule, itemsToRemove) => {

        const products = gaEcommerce.rules.findProducts(rule.items);

        products.map((product) => {
            const productElement = gaEcommerce.core.getProductElementById(product.id);

            itemsToRemove.push({ element: productElement, dataOverride: product });
        });
    },

    /*
     * Process a rule that might "replace" a product
     *
     * @param {object} rule
     * @param {array} itemsToRemove
     * @param {array} itemsToAdd
     *
     * @returns {void}
     */
    processReplaceRule: (rule, itemsToRemove, itemsToAdd) => {
        const products = gaEcommerce.rules.findProducts(rule.items);

        // If we don't have any of the things we need to replace, just bail
        if (products.length < 1) {
            return;
        }

        products.map(function(product) {
            const productElementToRemove = gaEcommerce.core.getProductElementById(product.id);

            if (productElementToRemove === null) {
                return;
            }

            itemsToRemove.push({ element: productElementToRemove, dataOverride: product });

            const fields = Object.keys(rule.modifications);

            // If we have no fields, we just want to reset to the default
            if (fields.length < 1) {
                itemsToAdd.push(productElementToRemove);
                return;
            }

            fields.map(function (field) {

                const newValue = rule.modifications[field];

                switch (field) {
                    case 'variant':

                        const currentVariant = product.variant.toLowerCase().replace(' ', '-'),
                              newVariant = newValue.toLowerCase().replace(' ', '-'),
                              newProductId = product.id.replace(currentVariant, newVariant),
                              replacementProduct = gaEcommerce.core.getProductElementById(newProductId);

                        if (replacementProduct !== null) {
                            itemsToAdd.push(gaEcommerce.core.getProductElementById(newProductId));
                        }
                        break;
                }
            });
        });
    },

    /*
     * Process a rule that might "update" a product
     *
     * @param {object} rule
     * @param {array} itemsToRemove
     * @param {array} itemsToAdd
     *
     * @returns {void}
     */
    processUpdateRule: (rule, itemsToRemove, itemsToAdd) => {
        const products = gaEcommerce.rules.findProducts(rule.items);

        if (products.length < 1) {
            return;
        }

        products.map(function(product) {
            const productElement = gaEcommerce.core.getProductElementById(product.id);

            if (productElement === null) {
                return;
            }

            itemsToRemove.push({
                element: productElement,
                dataOverride: product,
                processRules: false
            });

            itemsToAdd.push({
                element: productElement,
                dataOverride: rule.modifications,
                processRules: false
            });
        });
    },

    /*
     * Creates if products don't exist, updates if they do (if required)
     *
     * @param {object} element
     * @param {object} rule
     * @param {array} itemsToRemove
     * @param {array} itemsToAdd
     *
     * @returns {void}
     */
    processPutRule: (element, rule, itemsToRemove, itemsToAdd) => {

        const products = gaEcommerce.rules.findProducts(rule.items);

        if (products.length < 1) {
            itemsToAdd.push({
                element: element,
                dataOverride: rule.modifications
            });
            return;
        }

        products.map(function(product) {

            const productElement = gaEcommerce.core.getProductElementById(product.id);

            if (productElement === null) {
                return;
            }

            // Check if changes are required
            const isDirty = false;
            Object.keys(rule.modifications).map(function (field) {
                if (
                    typeof product[field] === 'undefined'
                    || product[field] !== rule.modifications[field]
                ) {
                    isDirty = true;
                }
            });

            if (isDirty) {
                itemsToRemove.push({
                    element: productElement,
                    dataOverride: product,
                    processRules: false
                });

                itemsToAdd.push({
                    element: productElement,
                    dataOverride: rule.modifications,
                    processRules: false
                });
            }
        });
    },

    /*
     * Manage bundles element depending on number of bundleable products
     *
     * @param {object} rule
     * @param {array} itemsToRemove
     * @param {array} itemsToAdd
     *
     * @returns {void}
     */
    processUpdateBundleRule: (rule, itemsToRemove, itemsToAdd) => {
        if (!gaEcommerce.core.isProductBundleable(rule.id)) {
            return;
        }

        const callFeatures = gaEcommerce.rules.findProducts([{ category: "Add-ons/Phone/Call Features" }]);
        let addedFeatureElement;

        if (rule.triggerOn === 'add') {
            callFeatures.push(gaEcommerce.util.parseJsonObj(
                gaEcommerce.core.getProductElementById(rule.id).dataset.ecomProduct
            ));
        }

        // Filter out the non bundleables
        const bundleable = callFeatures.filter((callFeature) => {
            // If we're removing one, then skip it
            if (rule.triggerOn === 'remove' && callFeature.id === rule.id) {
                return false;
            }

            // We know the one we're adding is bundleable already
            return callFeature.id === rule.id
                || gaEcommerce.core.isProductBundleable(callFeature.id);
        });

        if (rule.triggerOn === 'remove') {

            // If we're going to have no selected bundleable call features
            // Remove the bundle coupon from all elements
            if (bundleable.length === 0) {
                gaEcommerce.rules.removeBundleCouponFromAll();
                return;
            }

            // If we're removing, and down to just 1 element
            if (bundleable.length === 1) {
                gaEcommerce.rules.disqualifyFromCallFeatureBundle(itemsToRemove, itemsToAdd);
                // Remove the bundle coupon from the remaining element
                gaEcommerce.rules.removeBundleCoupon(
                    gaEcommerce.core.getProductElementById(bundleable[0].id)
                );
                return;
            }
        }

        if (rule.triggerOn === 'add') {
            if (bundleable.length === 1) {
                gaEcommerce.rules.addBundleCouponToAllOthers(rule.id);
                return;
            }

            if (bundleable.length === 2) {
                gaEcommerce.rules.addBundleCouponToAll();
            }
        }

        // If after updating, we are going to have more than 1 bundleable call feature
        // Then we need to add the corresponding bundle
        if (bundleable.length > 1) {

            const productElement = gaEcommerce.core.getProductElementById(
                'Plusnet_Incentives_Phone_Call-Features-Bundle_' + bundleable.length + "-Features"
            ),
            productElementData = gaEcommerce.util.parseJsonObj(productElement.dataset.ecomProduct);

            itemsToAdd.push({
                element: productElement,
                dataOverride: {
                    price: gaEcommerce.rules.calculateBundleValue(bundleable, productElementData)
                }
            });

            gaEcommerce.rules.qualifyForCallFeatureBundle(itemsToRemove, itemsToAdd);
        }
    },

    /*
     * Updates the activation fees from the basket
     *
     * @returns {void}
     */
    processUpdateActivationRule: () => {
        const updateActivation = () => {
            const itemsToRemove = [],
                  itemsToAdd = [];
            let activationFee = null,
                newLineFee = null;

            gaEcommerce.util.getBasket().real.data.activeUpfrontCharges.items.map((item) => {
                if (item.name.indexOf('Installation charge') !== -1) {
                    newLineFee = item;
                }

                if (item.name.indexOf('Activation fee') !== -1) {
                    activationFee = item;
                }
            });

            gaEcommerce.rules.handleActivationFee(activationFee, itemsToRemove, itemsToAdd);
            gaEcommerce.rules.handleNewLineFee(newLineFee, itemsToRemove, itemsToAdd);

            gaEcommerce.util.getBasket().processItems(
                itemsToAdd, itemsToRemove, 'non-interaction', true
            );
        };

        // If we already have the real basket initialised, do it now
        if (gaEcommerce.util.isDefined(
            gaEcommerce.util.getBasket(), ['real', 'data', 'activeUpfrontCharges', 'items'])
        ) {
            updateActivation();
        } else {
            // Otherwise, delay it until we have the basket initialised
            window.addEventListener(
                'ecommerceRealBasketInitialised', updateActivation, { once: true }
            );
        }
    },

    /*
     * Create, remove, update activation fee as required
     *
     * @param {null|object} activationFee
     * @param {array} itemsToRemove
     * @param {array} itemsToAdd
     *
     * @returns {void}
     */
    handleActivationFee: (activationFee, itemsToRemove, itemsToAdd) => {
        gaEcommerce.rules.handleSetupFee(
            activationFee,
            'Setup/Broadband/Service',
            'Plusnet_Setup_Broadband_Activation-Fee',
            itemsToRemove,
            itemsToAdd
        );
    },

    /*
     * Create, remove, update new line fee as required
     *
     * @param {null|object} newLineFee
     * @param {array} itemsToRemove
     * @param {array} itemsToAdd
     *
     * @returns {void}
     */
    handleNewLineFee: (newLineFee, itemsToRemove, itemsToAdd) => {
        gaEcommerce.rules.handleSetupFee(
            newLineFee,
            'Setup/Phone/Phone Line',
            'Openreach_Setup_Phone_New-Line-Install',
            itemsToRemove,
            itemsToAdd
        );
    },

    /*
     * Create, remove, update a generic fee as required
     *
     * @param {object} newFee
     * @param {string} category
     * @param {string} id
     * @param {array} itemsToRemove
     * @param {array} itemsToAdd
     *
     * @returns {void}
     */
    handleSetupFee: (newFee, category, id, itemsToRemove, itemsToAdd) => {
        // If we shouldn't have a fee, remove any that exist
        if (newFee === null) {
            const removeFee = {
                items: [{ category: category }]
            };
            gaEcommerce.rules.processRemoveRule(removeFee, itemsToRemove);

            return;
        }

        // If we should have a fee, issue a "put" rule, so one will either be created or updated as required
        let price = newFee.price,
            coupon = '';

        if (
            typeof newFee.discount !== 'undefined'
            && typeof newFee.discount.discountPrice !== 'undefined'
        ) {
            price = newFee.discount.discountPrice;
            coupon = newFee.discount.discountId;
        }

        const replaceFee = {
            items: [{ category: category }],
            modifications: {
                coupon: coupon,
                price: price
            }
        };

        gaEcommerce.rules.processPutRule(
            gaEcommerce.core.getProductElementById(id), replaceFee, itemsToRemove, itemsToAdd
        );
    },

    /*
     * Calculate the value of the discount applied to the bundled features (Should be a negative number)
     *
     * @param {array} bundleable
     * @param {object} productElementData
     *
     * @returns {string}
     */
    calculateBundleValue: (bundleable, productElementData) => 
        (
            parseFloat(productElementData.price)
            - gaEcommerce.rules.getBundleTotalPrice(bundleable)
        ).toFixed(2)
    ,

    /*
     * Gets the sum of the full prices of the selected call feature
     *
     * @param {array} bundleable
     * @returns {float}
     */
    getBundleTotalPrice: (bundleable) =>
        bundleable.reduce((totalPrice, callFeature) => {
            return totalPrice + parseFloat(callFeature.price);
        }, 0.00)
    ,

    /*
     * Process updates to qualify a customer for a call feature bundle
     *
     * @param {array} itemsToRemove
     * @param {array} itemsToAdd
     */
    qualifyForCallFeatureBundle: (itemsToRemove, itemsToAdd) => {
        const allBundleableFeatures = gaEcommerce.core.getBundleableCallFeatures(),
              items = Object.keys(allBundleableFeatures).map((id) => {
                  return {
                      id: id,
                      coupon:''
                  }
              });
        gaEcommerce.rules.processUpdateRule(
            {
                items: items,
                modifications: {
                    coupon: 'bundle'
                }
            },
            itemsToRemove,
            itemsToAdd
        );
    },

    /*
     * Process updates to disqualify the customer from a call feature bundle
     *
     * @param {array} itemsToRemove
     * @param {array} itemsToAdd
     *
     * @returns {void}
     */
    disqualifyFromCallFeatureBundle: (itemsToRemove, itemsToAdd) => {
        // then we can remove the bundle
        gaEcommerce.rules.processRemoveRule(
            { items: [{ category: 'Incentives/Phone/Bundles' }] },
            itemsToRemove
        );

        // remove the bundle coupon from any remaining products
        gaEcommerce.rules.processUpdateRule(
            {
                items: [{
                    category: 'Add-ons/Phone/Call Features',
                    coupon: 'bundle'
                }],
                modifications: {
                    coupon: ''
                }
            },
            itemsToRemove,
            itemsToAdd
        );
    },

    /*
     * Loop through all bundleable call features and call the callback on each
     *
     * @param {function} callback
     * @returns {void}
     */
    doForAllCallFeatures: (callback) => {
        const allBundleableFeatures = gaEcommerce.core.getBundleableCallFeatures();
        Object.keys(allBundleableFeatures).map(function (id) {
            callback(allBundleableFeatures[id]);
        });
    },

    /*
     * Adds the "bundle" coupon a call features data-ecom-product attribute
     *
     * @param {object} element
     * @returns {void}
     */
    addBundleCoupon: (element) => {
        gaEcommerce.core.changeDataEcomProductAttribute(element, { coupon: "bundle" });
    },

    /*
     * Adds the "bundle" coupon for all call features data-ecom-product attributes except the given one
     *
     * @param {string|int} exceptId
     * @returns {void}
     */
    addBundleCouponToAll: () => {
        gaEcommerce.rules.doForAllCallFeatures(gaEcommerce.rules.addBundleCoupon);
    },

    /*
     * Adds the "bundle" coupon for all call features data-ecom-product attributes except the given one
     *
     * @param {string|int} exceptId
     * @returns {void}
     */
    addBundleCouponToAllOthers: (exceptId) => {
        const allBundleableFeatures = gaEcommerce.core.getBundleableCallFeatures();
        Object.keys(allBundleableFeatures).filter(function (id) {
            return id !== exceptId;
        }).map(function (id) {
            gaEcommerce.rules.addBundleCoupon(allBundleableFeatures[id]);
        });
    },

    /*
     * Remove the "bundle" coupon from all call features data-ecom-product attribute
     *
     * @param {object} element
     * @returns {void}
     */
    removeBundleCoupon: (element) => {
        gaEcommerce.core.changeDataEcomProductAttribute(element, { coupon: '' });
    },

    /*
     * Remove the "bundle" coupon from all call features data-ecom-product attribute
     *
     * @returns {void}
     */
    removeBundleCouponFromAll: () => {
        gaEcommerce.rules.doForAllCallFeatures(gaEcommerce.rules.removeBundleCoupon);
    },

    /*
     * Find products
     *
     * @param {array} searchList
     * @returns {array} list of products matching query
     */
    findProducts: (searchList) => {

        const products = gaEcommerce.util.getBasket().getBasketContents(),
              found = [];

        searchList.map((search) => {
            return products.map(gaEcommerce.util.parseJsonArray).reduce((filtered, product) => {
                let matched = true;

                Object.keys(search).map((field) => {
                    if (!gaEcommerce.rules.matchByField(field, product, search[field])) {
                        matched = false;
                    }
                });

                if (matched) {
                    filtered.push(product);
                }

                return filtered;

            }, found);

        });

        return found;
    },

    /*
     * Match the product by the field value
     *
     * @param {string} field
     * @param {object} product
     * @param {string} value
     *
     * @returns {boolean}
     */
    matchByField: (field, product, value) => {
        if (typeof value === 'object'
            && value.type === 'undefined'
            && typeof product[field] === 'undefined'
        ) {
            return true;
        }

        if (typeof product[field] !== 'undefined'
            && gaEcommerce.rules.getMatcher(value)(product[field])
        ) {
            return true;
        }

        return false;
    },

    /*
     * Get the relevant matcher for the value
     *
     * @param {string|object} value
     * @returns {(function(string): boolean)}
     */
    getMatcher: (value) => {
        if (typeof value === 'object') {
            switch (value.type) {
                case 'regex':
                    return gaEcommerce.util.getRegexMatcher(value.pattern);
                default:
            }
        }

        return gaEcommerce.util.getStringMatcher(value);
    },

    /*
     * Category to match call plans
     *
     * @returns {string}
     */
    matchCallPlan: () =>
        'Add-ons/Phone/Call Plans'
    ,

    /*
     * Category to match Broadband
     *
     * @returns {object}
     */
    matchBroadband: () => (
        { type: "regex", pattern: "^Services/Broadband" }
    )
};

export default rules;
